(function installCcmLivePreviewV3() {
  if (customElements.get('ccm-live-preview-v3')) return;

  const APPEARANCE_VERSION = '20260905-neutral-studio-v1';
  const HANDOFF_KEY = 'ccm-live-preview-v3-handoff-v1';
  const DRAG_HINT_KEY = 'ccm-live-preview-v3-drag-dismissed-v1';
  const REAR_HINT_KEY = 'ccm-live-preview-v3-rear-shown-v1';
  const VIEWER_COMMAND_SCHEMA = 'ccm-storefront-viewer-command/v1';
  const HANDOFF_MAX_AGE_MS = 2 * 60 * 60 * 1000;
  const MAX_FILE_BYTES = 10 * 1024 * 1024;
  const MAX_IMAGE_PIXELS = 40000000;
  const ACCEPTED_TYPES = new Set([
    'image/jpeg',
    'image/png',
    'image/webp',
    'image/heic',
    'image/heif',
  ]);

  function createUuid() {
    if (window.crypto?.randomUUID) return window.crypto.randomUUID();
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (character) => {
      const random = window.crypto?.getRandomValues
        ? window.crypto.getRandomValues(new Uint8Array(1))[0] % 16
        : Math.floor(Math.random() * 16);
      const value = character === 'x' ? random : (random & 0x3) | 0x8;
      return value.toString(16);
    });
  }

  function safeJsonParse(value) {
    try {
      return JSON.parse(value);
    } catch (_error) {
      return null;
    }
  }

  function wait(milliseconds) {
    return new Promise((resolve) => window.setTimeout(resolve, milliseconds));
  }

  class CcmLivePreviewV3 extends HTMLElement {
    constructor() {
      super();
      this.v3Active = false;
      this.file = null;
      this.objectUrl = '';
      this.legacyPhoto = null;
      this.legacyUploadPromise = null;
      this.createPromise = null;
      this.createAbortController = null;
      this.pollTimer = null;
      this.pollStartedAt = 0;
      this.pollInFlight = false;
      this.configureTimer = null;
      this.configureInFlight = false;
      this.configureQueued = false;
      this.sessionId = '';
      this.previewToken = '';
      this.statusPath = '';
      this.configurePath = '';
      this.cartAssociationPath = '';
      this.rendererPath = '';
      this.posterPath = '';
      this.posterFullUrl = '';
      this.posterThumbnailUrl = '';
      this.rendererSlowTimer = null;
      this.guidanceTimer = null;
      this.posterTimer = null;
      this.currentRevision = -1;
      this.inferenceStatus = '';
      this.statusValue = '';
      this.lastSentSignature = '';
      this.loadedRendererKey = '';
      this.rendererFrame = null;
      this.pendingRendererFrame = null;
      this.expectedRendererOrigin = '';
      this.pendingRendererOrigin = '';
      this.currentStageKey = 'intake';
      this.currentStageIndex = 1;
      this.currentStageLabel = '';
      this.generationKind = 'initial';
      this.lastWorkingSignature = '';
      this.lastPreviewStateSignature = '';
      this.pendingLedGuidance = false;
      this.posterCaptureRevision = -1;
      this.posterUploadRevision = -1;
      this.posterRetryRevision = -1;
      this.allowNextSubmit = false;
      this.cartConfigurationPending = false;
      this.associatedCartToken = '';
      this.analyticsEmitted = new Set();
      this.unsubscribeVariant = null;
      this.handleWindowMessage = this.handleWindowMessage.bind(this);
      this.handleDocumentClick = this.handleDocumentClick.bind(this);
      this.handleDocumentInput = this.handleDocumentInput.bind(this);
      this.handleFormSubmit = this.handleFormSubmit.bind(this);
      this.handleHomepageNavigation = this.handleHomepageNavigation.bind(this);
      this.handlePageHide = this.handlePageHide.bind(this);
    }

    connectedCallback() {
      if (!this.rolloutEnabled()) return;

      this.v3Active = true;
      this.hidden = false;
      this.fileInput = this.querySelector('[data-v3-file-input]');
      this.sample = this.querySelector('[data-v3-sample]');
      this.invitation = this.querySelector('[data-v3-invitation]');
      this.working = this.querySelector('[data-v3-working]');
      this.sourceImage = this.querySelector('[data-v3-source-image]');
      this.overlayHeading = this.querySelector('[data-v3-overlay-heading]');
      this.overlayBody = this.querySelector('[data-v3-overlay-body]');
      this.progress = this.querySelector('[data-v3-progress]');
      this.progressTime = this.querySelector('[data-v3-progress-time]');
      this.rendererHost = this.querySelector('[data-v3-renderer]');
      this.guidance = this.querySelector('[data-v3-guidance]');
      this.guidanceHeading = this.querySelector('[data-v3-guidance-heading]');
      this.guidanceBody = this.querySelector('[data-v3-guidance-body]');
      this.statusHeading = this.querySelector('[data-v3-status-heading]');
      this.statusBody = this.querySelector('[data-v3-status-body]');
      this.changePhotoButton = this.querySelector('[data-v3-change-photo]');
      this.retryButton = this.querySelector('[data-v3-retry]');
      this.catalogViewer = this.querySelector('ccm-native-crystal-viewer');
      this.productForm = this.dataset.productFormId
        ? document.getElementById(this.dataset.productFormId)
        : null;
      const productFormHost = this.productForm?.closest('product-form');
      if (productFormHost) productFormHost.livePreviewV3Coordinator = this;
      this.desiredSelection = this.readSelection();
      this.stageLabels = {
        intake: this.dataset.stageIntake,
        background_removal: this.dataset.stageBackgroundRemoval,
        depth_estimation: this.dataset.stageDepthEstimation,
        engraving_field: this.dataset.stageEngravingField,
        field_packaging: this.dataset.stageFieldPackaging,
        crystal_compilation: this.dataset.stageCrystalCompilation,
      };

      this.activateSurface();
      this.bindControls();
      this.showInitial();
      this.emitAnalytics('live_preview_v3_opened');

      if (this.dataset.surface === 'pdp') {
        this.restoreHandoff();
        window.CCMOpenProductPreview = () => this.focusViewer();
      }
    }

    disconnectedCallback() {
      window.removeEventListener('message', this.handleWindowMessage);
      window.removeEventListener('pagehide', this.handlePageHide);
      document.removeEventListener('click', this.handleDocumentClick, true);
      document.removeEventListener('input', this.handleDocumentInput, true);
      document.removeEventListener('change', this.handleDocumentInput, true);
      this.productForm?.removeEventListener('submit', this.handleFormSubmit, true);
      this.homeProductLink?.removeEventListener('click', this.handleHomepageNavigation, true);
      this.unsubscribeVariant?.();
      this.stopPolling();
      window.clearTimeout(this.configureTimer);
      this.setCartConfigurationPending(false);
      window.clearTimeout(this.rendererSlowTimer);
      window.clearTimeout(this.guidanceTimer);
      window.clearTimeout(this.posterTimer);
      this.createAbortController?.abort();
      if (this.objectUrl) URL.revokeObjectURL(this.objectUrl);
    }

    get isActive() {
      return this.v3Active;
    }

    rolloutEnabled() {
      const mode = String(this.dataset.mode || 'off').toLowerCase();
      if (mode === 'on') return true;
      return mode === 'internal' && new URLSearchParams(window.location.search).get('preview_v3') === '1';
    }

    activateSurface() {
      if (this.dataset.surface === 'homepage') {
        this.closest('.theme-preview-popup')?.classList.add('is-v3-active');
      } else {
        this.closest('.product__preview')?.classList.add('is-v3-active');
        this.closest('product-info')?.querySelectorAll('.orientation-link').forEach((link) => {
          const url = new URL(link.href, window.location.origin);
          url.searchParams.set('preview_v3', '1');
          const currentUrl = new URL(window.location.href);
          ['preview_theme_id', 'view'].forEach((parameter) => {
            const value = currentUrl.searchParams.get(parameter);
            if (value) url.searchParams.set(parameter, value);
          });
          link.href = url.pathname + url.search;
        });
      }
    }

    bindControls() {
      this.querySelector('[data-v3-upload]')?.addEventListener('click', () => this.openFilePicker());
      this.querySelector('[data-v3-dismiss]')?.addEventListener('click', () => this.dismissInvitation());
      this.changePhotoButton?.addEventListener('click', () => this.openFilePicker());
      this.retryButton?.addEventListener('click', () => {
        if (this.file) this.startPreview(this.file);
        else this.openFilePicker();
      });
      this.fileInput?.addEventListener('change', () => {
        const file = this.fileInput.files?.[0];
        if (file) this.handleFile(file);
      });

      window.addEventListener('message', this.handleWindowMessage);
      window.addEventListener('pagehide', this.handlePageHide);
      document.addEventListener('click', this.handleDocumentClick, true);
      document.addEventListener('input', this.handleDocumentInput, true);
      document.addEventListener('change', this.handleDocumentInput, true);
      this.productForm?.addEventListener('submit', this.handleFormSubmit, true);
      if (this.dataset.surface === 'homepage') {
        this.homeProductLink = this.closest('.theme-preview-popup')?.querySelector('[data-target="product-url"]');
        this.homeProductLink?.addEventListener('click', this.handleHomepageNavigation, true);
      }

      if (
        this.dataset.surface === 'pdp'
        && typeof subscribe === 'function'
        && typeof PUB_SUB_EVENTS !== 'undefined'
        && PUB_SUB_EVENTS.variantChange
      ) {
        this.unsubscribeVariant = subscribe(PUB_SUB_EVENTS.variantChange, ({ data }) => {
          const section = this.closest('product-info');
          if (section?.dataset.section && data?.sectionId && section.dataset.section !== data.sectionId) return;
          if (data?.variant?.sku) this.updateSelection({ crystal_sku: data.variant.sku });
        });
      }
    }

    handleDocumentClick(event) {
      if (!this.v3Active || this.dataset.surface !== 'pdp') return;
      const button = event.target.closest('.product-upload-photo .theme-button-secondary');
      if (!button) return;
      const uploadBlock = button.closest('.product-upload-photo');
      if (
        uploadBlock?.dataset.productFormId
        && this.dataset.productFormId
        && uploadBlock.dataset.productFormId !== this.dataset.productFormId
      ) return;

      event.preventDefault();
      event.stopImmediatePropagation();
      this.openFilePicker();
    }

    handleDocumentInput(event) {
      if (!this.v3Active || this.dataset.surface !== 'pdp') return;
      const target = event.target;
      if (!(target instanceof HTMLInputElement) && !(target instanceof HTMLSelectElement)) return;
      const formId = target.getAttribute('form');
      if (formId && this.dataset.productFormId && formId !== this.dataset.productFormId) return;

      if (target.matches('variant-selects-extended input[data-base-sku]')) {
        this.updateSelection({ base_sku: target.dataset.baseSku || 'none' });
        return;
      }

      if (target.name === 'properties[Engrave Text]') {
        this.updateSelection({ engraving_text: target.value || '' });
        return;
      }

      if (target.name === 'properties[Font]') {
        this.updateSelection({ engraving_font: target.value || 'NotoSansKR' });
        return;
      }

      if (target.name === 'properties[Crystal SKU]') {
        this.updateSelection({ crystal_sku: target.value || this.dataset.initialSku || '' });
      }
    }

    controlValue(name, fallback = '') {
      const formId = this.dataset.productFormId;
      const form = formId ? document.getElementById(formId) : null;
      const associatedControl = formId
        ? document.querySelector(`[form="${CSS.escape(formId)}"][name="${CSS.escape(name)}"]`)
        : null;
      if (associatedControl) return associatedControl.value;
      const formControl = form?.querySelector(`[name="${CSS.escape(name)}"]`);
      return formControl ? formControl.value : fallback;
    }

    readSelection() {
      const scope = this.closest('product-info') || document;
      const selectedBase = scope.querySelector('variant-selects-extended input[data-base-sku]:checked');
      return {
        crystal_sku: this.controlValue('properties[Crystal SKU]', this.dataset.initialSku || ''),
        orientation: this.dataset.defaultOrientation || 'tower',
        base_sku: selectedBase?.dataset.baseSku || this.dataset.initialBaseSku || 'none',
        engraving_text: this.controlValue('properties[Engrave Text]', ''),
        engraving_font: this.controlValue('properties[Font]', 'NotoSansKR'),
      };
    }

    syncSelectionFromControls() {
      const selection = this.readSelection();
      this.desiredSelection = { ...this.desiredSelection, ...selection };
      return this.desiredSelection;
    }

    updateSelection(nextSelection) {
      const previousSignature = this.selectionSignature(this.desiredSelection);
      const previousBase = this.desiredSelection?.base_sku || 'none';
      this.desiredSelection = { ...this.desiredSelection, ...nextSelection };
      const nextSignature = this.selectionSignature(this.desiredSelection);
      if (previousSignature === nextSignature) return;

      if (nextSelection.crystal_sku && this.catalogViewer?.setSku) {
        this.catalogViewer.setSku(nextSelection.crystal_sku);
      }
      if (nextSelection.base_sku && this.catalogViewer) {
        this.catalogViewer.setAttribute('base-mode', nextSelection.base_sku);
      }
      if (
        nextSelection.base_sku
        && nextSelection.base_sku !== 'none'
        && nextSelection.base_sku !== previousBase
      ) {
        this.pendingLedGuidance = true;
      }
      if (this.sessionId) {
        this.setCartConfigurationPending(true);
        this.emitAnalytics('live_preview_v3_product_changed');
        this.scheduleConfigure();
      }
      this.persistHandoff();
    }

    dismissInvitation() {
      if (!this.invitation) return;
      this.invitation.classList.add('is-dismissed');
      this.changePhotoButton.textContent = this.dataset.copyUpload;
      this.changePhotoButton.hidden = false;
    }

    handlePageHide() {
      this.persistHandoff();
    }

    setSelection(nextSelection) {
      this.updateSelection(nextSelection);
    }

    selectionSignature(selection) {
      if (!selection) return '';
      return JSON.stringify([
        selection.crystal_sku || '',
        selection.orientation || '',
        selection.base_sku || 'none',
        selection.engraving_text || '',
        selection.engraving_font || 'NotoSansKR',
      ]);
    }

    cartSubmitButtons() {
      const buttons = new Set(this.productForm?.querySelectorAll('button[type="submit"]') || []);
      if (this.dataset.productFormId) {
        document
          .querySelectorAll(`button[type="submit"][form="${CSS.escape(this.dataset.productFormId)}"]`)
          .forEach((button) => buttons.add(button));
      }
      return [...buttons];
    }

    setCartConfigurationPending(pending) {
      this.cartConfigurationPending = Boolean(pending);
      this.cartSubmitButtons().forEach((button) => {
        const label = button.querySelector('.product-form__submit-label');
        if (this.cartConfigurationPending) {
          if (button.dataset.livePreviewV3Waiting === 'true') return;
          button.dataset.livePreviewV3Waiting = 'true';
          button.dataset.livePreviewV3OriginalDisabled = button.disabled ? 'true' : 'false';
          button.dataset.livePreviewV3OriginalAriaDisabled = button.hasAttribute('aria-disabled')
            ? button.getAttribute('aria-disabled')
            : '__missing__';
          if (label) button.dataset.livePreviewV3OriginalLabel = label.textContent;
          button.disabled = true;
          button.setAttribute('aria-disabled', 'true');
          button.setAttribute('aria-busy', 'true');
          if (label && this.dataset.copyUpdatingHeading) {
            label.textContent = this.dataset.copyUpdatingHeading;
          }
          return;
        }

        if (button.dataset.livePreviewV3Waiting !== 'true') return;
        button.disabled = button.dataset.livePreviewV3OriginalDisabled === 'true';
        if (button.dataset.livePreviewV3OriginalAriaDisabled === '__missing__') {
          button.removeAttribute('aria-disabled');
        } else {
          button.setAttribute('aria-disabled', button.dataset.livePreviewV3OriginalAriaDisabled);
        }
        button.removeAttribute('aria-busy');
        if (label && Object.prototype.hasOwnProperty.call(button.dataset, 'livePreviewV3OriginalLabel')) {
          label.textContent = button.dataset.livePreviewV3OriginalLabel;
        }
        delete button.dataset.livePreviewV3Waiting;
        delete button.dataset.livePreviewV3OriginalDisabled;
        delete button.dataset.livePreviewV3OriginalAriaDisabled;
        delete button.dataset.livePreviewV3OriginalLabel;
      });
    }

    selectionIsReadyForCart() {
      return (
        this.selectionSignature(this.desiredSelection) === this.lastSentSignature
        && !this.configureTimer
        && !this.configureInFlight
        && !this.configureQueued
        && !this.pendingRendererFrame
        && this.dataset.state === 'ready'
      );
    }

    selectionUpdateIsPending() {
      return (
        this.cartConfigurationPending
        || this.selectionSignature(this.desiredSelection) !== this.lastSentSignature
        || Boolean(this.configureTimer)
        || this.configureInFlight
        || this.configureQueued
        || (this.generationKind === 'update' && Boolean(this.pendingRendererFrame))
      );
    }

    mountExternalUploader(container) {
      if (!this.v3Active || !container) return;
      const button = document.createElement('button');
      const heading = document.createElement('strong');
      const body = document.createElement('span');
      button.type = 'button';
      button.className = 'ccm-live-preview-v3__external-upload';
      heading.textContent = this.dataset.copyUpload;
      body.textContent = this.dataset.copyInitialBody;
      button.append(heading, body);
      button.addEventListener('click', () => this.openFilePicker());
      container.replaceChildren(button);
      container.addEventListener('dragover', (event) => {
        event.preventDefault();
        container.classList.add('is-dragging');
      });
      container.addEventListener('dragleave', () => container.classList.remove('is-dragging'));
      container.addEventListener('drop', (event) => {
        event.preventDefault();
        container.classList.remove('is-dragging');
        const file = event.dataTransfer?.files?.[0];
        if (file) this.handleFile(file);
      });
    }

    openFilePicker() {
      if (!this.v3Active) {
        this.openLegacyPicker();
        return;
      }
      this.openV3Picker();
    }

    resetAndOpen() {
      if (!this.v3Active) {
        this.openLegacyPicker();
        return;
      }
      this.stopPolling();
      this.createAbortController?.abort();
      this.sessionId = '';
      this.previewToken = '';
      this.statusPath = '';
      this.configurePath = '';
      this.cartAssociationPath = '';
      this.rendererPath = '';
      this.posterPath = '';
      this.posterFullUrl = '';
      this.posterThumbnailUrl = '';
      window.clearTimeout(this.rendererSlowTimer);
      window.clearTimeout(this.guidanceTimer);
      window.clearTimeout(this.posterTimer);
      this.currentRevision = -1;
      this.lastSentSignature = '';
      this.loadedRendererKey = '';
      this.rendererFrame = null;
      this.pendingRendererFrame = null;
      this.posterCaptureRevision = -1;
      this.posterUploadRevision = -1;
      this.posterRetryRevision = -1;
      this.lastPreviewStateSignature = '';
      this.associatedCartToken = '';
      this.rendererHost?.replaceChildren();
      this.setCartConfigurationPending(false);
      this.clearLineProperties();
      this.file = null;
      this.legacyPhoto = null;
      this.legacyUploadPromise = null;
      if (this.objectUrl) URL.revokeObjectURL(this.objectUrl);
      this.objectUrl = '';
      try {
        sessionStorage.removeItem(HANDOFF_KEY);
      } catch (_error) {
        // Storage can be unavailable in strict privacy modes.
      }
      this.showInitial();
      this.openFilePicker();
    }

    createDeferred() {
      let resolve;
      let reject;
      const promise = new Promise((resolvePromise, rejectPromise) => {
        resolve = resolvePromise;
        reject = rejectPromise;
      });
      return { promise, resolve, reject };
    }

    async openV3Picker() {
      const generation = this.photoGeneration || 0;
      try {
        const choice = await window.NativePhotoCompat.choose();
        if (!choice || generation !== (this.photoGeneration || 0)) return;
        this.nativeChoice = choice;
        await this.handleFile(choice.file, { nativeAsset: choice.asset });
      } catch (error) { this.showError(error.message || this.dataset.copyErrorBody); }
    }

    async openLegacyPicker() {
      const generation = this.photoGeneration || 0;
      try {
        const choice = await window.NativePhotoCompat.choose();
        if (!choice || generation !== (this.photoGeneration || 0)) return;
        const data = window.NativePhotoCompat.metadata(choice);
        this.nativeAsset = choice.asset;
        this.setLineProperty('Photo', data.url, false);
        this.setLineProperty('_media_asset_id', choice.asset.asset_id, true);
        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.filestackFileChoiced, {data, formId:this.dataset.productFormId});
        }
      } catch (error) { this.showError(error.message || this.dataset.copyErrorBody); }
    }

    async handleFile(file, options = {}) {
      const validationMessage = await this.validateFile(file);
      if (validationMessage) {
        this.showError(validationMessage);
        return;
      }

      this.file = file;
      if (this.objectUrl) URL.revokeObjectURL(this.objectUrl);
      this.objectUrl = URL.createObjectURL(file);
      this.clearLineProperties();
      this.legacyPhoto = null;
      this.associatedCartToken = '';
      this.legacyUploadPromise = options.legacyUploadPromise
        || this.uploadLegacyPhoto(file).catch(() => null);
      this.generationKind = 'initial';
      this.currentStageKey = 'intake';
      this.currentStageIndex = 1;
      this.currentStageLabel = this.stageLabels.intake || this.dataset.copyProcessingHeading;
      this.showWorking(
        this.currentStageLabel,
        this.dataset.copyProcessingBody,
        this.currentStageIndex,
        this.generationKind,
      );
      this.emitPreviewState('creating');
      this.dispatchEvent(new CustomEvent('ccm:live-preview-v3:file-selected', {
        detail: { thumbnailUrl: this.objectUrl },
        bubbles: true,
      }));
      this.startPreview(file).catch(() => {});
    }

    async validateFile(file) {
      const type = String(file.type || '').toLowerCase();
      const extension = String(file.name || '').split('.').pop()?.toLowerCase();
      const extensionAccepted = extension === 'heic' || extension === 'heif';
      if ((!ACCEPTED_TYPES.has(type) && !extensionAccepted) || !file.size) {
        return this.dataset.copyInvalidFile;
      }
      if (file.size > MAX_FILE_BYTES) return this.dataset.copyFileTooLarge;

      if (type === 'image/heic' || type === 'image/heif' || extensionAccepted) return '';
      if (!window.createImageBitmap) return '';
      try {
        const bitmap = await createImageBitmap(file);
        const pixels = bitmap.width * bitmap.height;
        bitmap.close();
        if (pixels > MAX_IMAGE_PIXELS) return this.dataset.copyFileTooLarge;
      } catch (_error) {
        return this.dataset.copyInvalidFile;
      }
      return '';
    }

    startPreview(file) {
      this.stopPolling();
      this.createAbortController?.abort();
      this.createAbortController = new AbortController();
      this.sessionId = createUuid();
      this.previewToken = '';
      this.statusPath = '';
      this.configurePath = '';
      this.cartAssociationPath = '';
      this.rendererPath = '';
      this.posterPath = '';
      this.posterFullUrl = '';
      this.posterThumbnailUrl = '';
      this.currentRevision = -1;
      this.inferenceStatus = '';
      this.statusValue = '';
      this.syncSelectionFromControls();
      this.lastSentSignature = this.selectionSignature(this.desiredSelection);
      this.loadedRendererKey = '';
      this.posterCaptureRevision = -1;
      this.posterUploadRevision = -1;
      this.posterRetryRevision = -1;
      this.pollStartedAt = Date.now();
      this.emitAnalytics('live_preview_v3_upload_started');

      const body = new FormData();
      body.append('image', file, file.name || 'upload');
      body.append('client_session_id', this.sessionId);
      body.append('crystal_sku', this.desiredSelection.crystal_sku);
      body.append('orientation', this.desiredSelection.orientation || 'tower');
      body.append('base_sku', this.desiredSelection.base_sku || 'none');
      body.append('engraving_text', this.desiredSelection.engraving_text || '');
      body.append('engraving_font', this.desiredSelection.engraving_font || 'NotoSansKR');

      this.createPromise = this.requestJson(this.dataset.createPath, {
        method: 'POST',
        body,
        signal: this.createAbortController.signal,
      })
        .then((payload) => {
          this.applyResponse(payload);
          this.emitAnalytics('live_preview_v3_session_created');
          this.persistHandoff();
          if (payload.status !== 'ready') this.schedulePoll();
          return payload;
        })
        .catch((error) => {
          if (error.name === 'AbortError') return null;
          this.handlePreviewFailure(error);
          throw error;
        });

      return this.createPromise;
    }

    async requestJson(path, options = {}) {
      const requestHeaders = {
        Accept: 'application/json',
        ...(options.body instanceof FormData ? {} : { 'Content-Type': 'application/json' }),
        ...(options.headers || {}),
      };
      const payload = await window.StorefrontRequest.run(async (signal) => {
        const response = await fetch(path, {
          credentials: 'same-origin',
          cache: 'no-store',
          ...options,
          headers: requestHeaders,
          signal,
        });
        const payload = JSON.parse(await response.text());
        if (!payload || typeof payload !== 'object' || Array.isArray(payload) || Object.keys(payload).length === 0) throw new Error('Invalid preview response');
        if (!response.ok) {
          const error = new Error(payload.message || this.dataset.copyErrorBody);
          error.status = response.status;
          throw error;
        }
        return payload;
      }, { signal: options.signal });
      return payload;
    }

    applyResponse(payload) {
      const revision = Number(payload.compilation_revision);
      if (Number.isFinite(revision) && revision < this.currentRevision) return false;
      if (Number.isFinite(revision)) this.currentRevision = revision;

      this.sessionId = payload.session_id || payload.id || this.sessionId;
      this.previewToken = payload.preview_token || this.previewToken;
      this.statusPath = payload.status_path || this.statusPath;
      this.configurePath = payload.configure_path || this.configurePath;
      this.cartAssociationPath = payload.cart_association_path || this.cartAssociationPath;
      this.rendererPath = payload.renderer_path || this.rendererPath;
      this.posterPath = payload.poster_path || this.posterPath;
      this.statusValue = payload.status || this.statusValue;
      this.inferenceStatus = payload.inference_status || this.inferenceStatus;
      if (payload.poster?.appearance_version === APPEARANCE_VERSION) {
        this.applyPoster(payload.poster);
      } else if (payload.poster && this.posterFullUrl) {
        this.invalidatePoster();
      }

      if (payload.status === 'failed') {
        throw new Error(payload.error?.message || payload.message || this.dataset.copyErrorBody);
      }

      if (payload.status === 'ready') {
        if (this.selectionSignature(this.desiredSelection) !== this.lastSentSignature) {
          this.scheduleConfigure(0);
        } else {
          this.loadRenderer();
        }
      } else {
        this.showApiStage(payload.stages || []);
        if (this.inferenceStatus === 'ready') this.scheduleConfigure();
      }

      this.persistHandoff();
      return true;
    }

    showApiStage(stages) {
      const active = stages.find((stage) => stage?.status === 'active');
      const nextPending = stages.find((stage) => stage?.status === 'pending');
      const latestComplete = [...stages].reverse().find((stage) => stage?.status === 'complete');
      const phaseByStage = {
        intake: 1,
        background_removal: 1,
        depth_estimation: 2,
        engraving_field: 2,
        field_packaging: 3,
        crystal_compilation: 4,
      };
      const stageKey = active?.key
        || nextPending?.key
        || latestComplete?.key
        || this.currentStageKey
        || 'intake';
      const reportedPhase = phaseByStage[stageKey] || 1;
      const phase = Math.max(this.currentStageIndex || 1, reportedPhase);
      const heading = this.stageLabels[stageKey] || this.dataset.copyProcessingHeading;
      const elapsed = this.pollStartedAt ? Date.now() - this.pollStartedAt : 0;
      const body = elapsed >= 60000 ? this.dataset.copySlowBody : this.dataset.copyProcessingBody;
      this.currentStageKey = stageKey;
      this.currentStageIndex = phase;
      this.currentStageLabel = heading;
      this.showWorking(heading, body, phase, this.generationKind);
      this.emitPreviewState(elapsed >= 60000 ? 'slow' : 'creating');
    }

    schedulePoll() {
      if (!this.v3Active || !this.statusPath || this.statusValue === 'ready' || this.statusValue === 'failed') return;
      window.clearTimeout(this.pollTimer);
      const elapsed = this.pollStartedAt ? Date.now() - this.pollStartedAt : 0;
      this.pollTimer = window.setTimeout(() => this.poll(), elapsed >= 60000 ? 2000 : 1000);
    }

    async poll() {
      if (this.pollInFlight || !this.statusPath || !this.previewToken) return;
      this.pollInFlight = true;
      try {
        const payload = await this.requestJson(this.statusPath, {
          method: 'POST',
          body: JSON.stringify({ preview_token: this.previewToken }),
        });
        this.applyResponse(payload);
        if (payload.status !== 'ready' && payload.status !== 'failed') this.schedulePoll();
      } catch (error) {
        this.handlePreviewFailure(error);
      } finally {
        this.pollInFlight = false;
      }
    }

    stopPolling() {
      window.clearTimeout(this.pollTimer);
      this.pollTimer = null;
      this.pollInFlight = false;
    }

    scheduleConfigure(delay = 500) {
      if (!this.sessionId) return;
      window.clearTimeout(this.configureTimer);
      this.configureTimer = window.setTimeout(() => {
        this.configureTimer = null;
        this.configure();
      }, delay);
    }

    async configure() {
      if (!this.configurePath || !this.previewToken) return;
      if (this.configureInFlight) {
        this.configureQueued = true;
        return;
      }
      if (this.inferenceStatus !== 'ready' && this.statusValue !== 'ready' && this.statusValue !== 'compiling') {
        return;
      }

      this.syncSelectionFromControls();
      const signature = this.selectionSignature(this.desiredSelection);
      if (signature === this.lastSentSignature) {
        if (this.dataset.state === 'ready' && !this.pendingRendererFrame) {
          this.setCartConfigurationPending(false);
        }
        return;
      }

      this.configureInFlight = true;
      this.configureQueued = false;
      this.generationKind = 'update';
      this.currentStageKey = 'crystal_compilation';
      this.currentStageIndex = 4;
      this.currentStageLabel = this.dataset.copyUpdatingHeading;
      this.showWorking(
        this.dataset.copyUpdatingHeading,
        this.dataset.copyProcessingBody,
        4,
        this.generationKind,
      );
      try {
        const payload = await this.requestJson(this.configurePath, {
          method: 'PATCH',
          body: JSON.stringify({
            preview_token: this.previewToken,
            crystal_sku: this.desiredSelection.crystal_sku,
            orientation: this.desiredSelection.orientation || 'tower',
            base_sku: this.desiredSelection.base_sku || 'none',
            engraving_text: this.desiredSelection.engraving_text || '',
            engraving_font: this.desiredSelection.engraving_font || 'NotoSansKR',
          }),
        });
        this.lastSentSignature = signature;
        this.pollStartedAt = Date.now();
        this.applyResponse(payload);
        this.schedulePoll();
      } catch (error) {
        if (error.status === 409 && this.inferenceStatus !== 'ready') {
          this.configureQueued = true;
        } else {
          this.handlePreviewFailure(error);
        }
      } finally {
        this.configureInFlight = false;
        if (
          this.configureQueued
          || this.selectionSignature(this.desiredSelection) !== this.lastSentSignature
        ) {
          this.scheduleConfigure();
        }
      }
    }

    loadRenderer() {
      if (!this.rendererPath) return;
      const rendererKey = `${this.rendererPath}|${this.currentRevision}`;
      if (rendererKey === this.loadedRendererKey) return;
      this.loadedRendererKey = rendererKey;

      const frame = document.createElement('iframe');
      frame.title = this.dataset.copyReadyHeading;
      frame.allow = 'fullscreen';
      frame.loading = 'eager';
      frame.referrerPolicy = 'no-referrer';
      frame.src = this.rendererPath;
      frame.className = 'is-v3-staging';
      frame.addEventListener('error', () => {
        if (frame === this.pendingRendererFrame) {
          frame.remove();
          this.pendingRendererFrame = null;
          this.showError(this.dataset.copyErrorBody, true);
        }
      }, { once: true });
      this.pendingRendererFrame?.remove();
      this.pendingRendererFrame = frame;
      this.pendingRendererOrigin = new URL(this.rendererPath, window.location.origin).origin;
      this.rendererHost.append(frame);
      this.rendererHost.hidden = false;
      this.showWorking(
        this.generationKind === 'update'
          ? this.dataset.copyUpdatingHeading
          : (this.currentStageLabel || this.dataset.copyProcessingHeading),
        this.dataset.copyProcessingBody,
        4,
        this.generationKind,
      );
      window.clearTimeout(this.rendererSlowTimer);
      this.rendererSlowTimer = window.setTimeout(() => {
        if (this.dataset.state !== 'ready') {
          this.showWorking(
            this.currentStageLabel || this.dataset.copyProcessingHeading,
            this.dataset.copySlowBody,
            4,
            this.generationKind,
          );
          this.emitPreviewState('slow');
        }
      }, 60000);
    }

    handleWindowMessage(event) {
      const fromPending = (
        this.pendingRendererFrame
        && event.source === this.pendingRendererFrame.contentWindow
        && event.origin === this.pendingRendererOrigin
      );
      const fromCurrent = (
        this.rendererFrame
        && event.source === this.rendererFrame.contentWindow
        && event.origin === this.expectedRendererOrigin
      );
      if (!fromPending && !fromCurrent) return;
      const payload = event.data || {};
      if (payload.type === 'ccm:crystal-viewer:ready') {
        const readyFrame = fromPending ? this.pendingRendererFrame : this.rendererFrame;
        if (!readyFrame) return;
        window.clearTimeout(this.rendererSlowTimer);
        if (fromPending) {
          const previousFrame = this.rendererFrame;
          this.rendererFrame = readyFrame;
          this.expectedRendererOrigin = this.pendingRendererOrigin;
          this.pendingRendererFrame = null;
          this.pendingRendererOrigin = '';
          readyFrame.classList.remove('is-v3-staging');
          readyFrame.classList.add('is-v3-active');
          if (previousFrame && previousFrame !== readyFrame) {
            previousFrame.classList.remove('is-v3-active');
            previousFrame.classList.add('is-v3-retiring');
            window.setTimeout(() => previousFrame.remove(), 450);
          }
        }
        this.sample.hidden = true;
        this.working.hidden = true;
        this.rendererHost.hidden = false;
        this.dataset.state = 'ready';
        if (this.selectionIsReadyForCart()) this.setCartConfigurationPending(false);
        this.statusHeading.textContent = this.dataset.copyReadyHeading;
        this.statusBody.textContent = this.dataset.copyReadyBody;
        this.changePhotoButton.textContent = this.dataset.copyChangePhoto;
        this.changePhotoButton.hidden = false;
        this.retryButton.hidden = true;
        this.emitPreviewState('ready');
        this.emitAnalytics('live_preview_v3_ready');
        this.persistHandoff();
        this.postViewerCommand('ccm:crystal-viewer:fit-selection', {
          crystalSku: this.desiredSelection?.crystal_sku || '',
          baseSku: this.desiredSelection?.base_sku || 'none',
        });
        this.startViewerIntroduction();
        if (this.pendingLedGuidance) {
          this.pendingLedGuidance = false;
          let dragDismissed = false;
          try {
            dragDismissed = localStorage.getItem(DRAG_HINT_KEY) === '1';
          } catch (_error) {
            dragDismissed = false;
          }
          window.setTimeout(() => {
            this.showGuidance(this.dataset.copyLedHeading, this.dataset.copyLedBody, 6500);
          }, dragDismissed ? 900 : 6800);
        }
      } else if (payload.type === 'ccm:crystal-viewer:bridge-ready') {
        if (fromCurrent || fromPending) {
          this.postViewerCommand('ccm:crystal-viewer:fit-selection', {
            crystalSku: this.desiredSelection?.crystal_sku || '',
            baseSku: this.desiredSelection?.base_sku || 'none',
          });
          this.startViewerIntroduction();
        }
      } else if (payload.type === 'ccm:crystal-viewer:interaction-start') {
        try {
          localStorage.setItem(DRAG_HINT_KEY, '1');
        } catch (_error) {
          // Guidance remains non-blocking when storage is unavailable.
        }
        this.hideGuidance();
      } else if (payload.type === 'ccm:crystal-viewer:rear-reached') {
        let shown = false;
        try {
          shown = localStorage.getItem(REAR_HINT_KEY) === '1';
          if (!shown) localStorage.setItem(REAR_HINT_KEY, '1');
        } catch (_error) {
          shown = false;
        }
        if (!shown) {
          this.showGuidance(this.dataset.copyRearHeading, this.dataset.copyRearBody, 7500);
        }
      } else if (payload.type === 'ccm:crystal-viewer:intro-complete') {
        let dismissed = false;
        try {
          dismissed = localStorage.getItem(DRAG_HINT_KEY) === '1';
        } catch (_error) {
          dismissed = false;
        }
        if (!dismissed) {
          this.showGuidance(this.dataset.copyDragHeading, this.dataset.copyDragBody, 6000);
        }
        this.schedulePosterCapture();
      } else if (payload.type === 'ccm:crystal-viewer:poster-ready') {
        this.uploadPoster(payload).catch(() => {});
      } else if (payload.type === 'ccm:crystal-viewer:poster-error') {
        if (Number(payload.revision) === this.currentRevision) {
          this.posterCaptureRevision = -1;
          if (!this.posterRetryRevision || this.posterRetryRevision !== this.currentRevision) {
            this.posterRetryRevision = this.currentRevision;
            this.schedulePosterCapture(1200);
          }
        }
      } else if (payload.type === 'ccm:crystal-viewer:error') {
        if (fromPending && this.rendererFrame) {
          this.pendingRendererFrame?.remove();
          this.pendingRendererFrame = null;
          this.working.hidden = true;
          this.showError(this.dataset.copyErrorBody, true);
        } else {
          this.handlePreviewFailure(new Error(this.dataset.copyErrorBody));
        }
      }
    }

    postViewerCommand(type, detail = {}) {
      if (!this.rendererFrame?.contentWindow) return;
      this.rendererFrame.contentWindow.postMessage(
        { schema: VIEWER_COMMAND_SCHEMA, type, ...detail },
        this.expectedRendererOrigin,
      );
    }

    startViewerIntroduction() {
      window.clearTimeout(this.posterTimer);
      let dismissed = false;
      try {
        dismissed = localStorage.getItem(DRAG_HINT_KEY) === '1';
      } catch (_error) {
        dismissed = false;
      }
      if (dismissed) {
        this.schedulePosterCapture(450);
        return;
      }
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        this.showGuidance(this.dataset.copyDragHeading, this.dataset.copyDragBody, 9000);
        this.schedulePosterCapture(450);
        return;
      }
      this.postViewerCommand('ccm:crystal-viewer:intro-orbit');
      this.posterTimer = window.setTimeout(() => this.schedulePosterCapture(0), 3200);
    }

    showGuidance(heading, body, duration = 7000) {
      if (!this.guidance || !heading) return;
      window.clearTimeout(this.guidanceTimer);
      this.guidanceHeading.textContent = heading;
      this.guidanceBody.textContent = body || '';
      this.guidance.hidden = false;
      this.guidanceTimer = window.setTimeout(() => this.hideGuidance(), duration);
    }

    hideGuidance() {
      window.clearTimeout(this.guidanceTimer);
      if (this.guidance) this.guidance.hidden = true;
    }

    schedulePosterCapture(delay = 500) {
      if (
        !this.rendererFrame
        || !this.posterPath
        || this.currentRevision < 0
        || this.posterCaptureRevision === this.currentRevision
      ) return;
      window.clearTimeout(this.posterTimer);
      this.posterTimer = window.setTimeout(() => {
        this.posterCaptureRevision = this.currentRevision;
        this.postViewerCommand('ccm:crystal-viewer:capture-poster', {
          revision: this.currentRevision,
        });
      }, delay);
    }

    async dataUrlToBlob(dataUrl) {
      const response = await fetch(dataUrl);
      if (!response.ok) throw new Error('Preview image conversion failed.');
      return response.blob();
    }

    async uploadPoster(payload) {
      const revision = Number(payload.revision);
      if (
        !this.posterPath
        || !this.previewToken
        || !Number.isFinite(revision)
        || revision !== this.currentRevision
        || revision === this.posterUploadRevision
        || !payload.posterDataUrl
        || !payload.thumbnailDataUrl
      ) return;
      this.posterUploadRevision = revision;
      try {
        const [poster, thumbnail] = await Promise.all([
          this.dataUrlToBlob(payload.posterDataUrl),
          this.dataUrlToBlob(payload.thumbnailDataUrl),
        ]);
        const body = new FormData();
        body.append('preview_token', this.previewToken);
        body.append('revision', String(revision));
        body.append('appearance_version', APPEARANCE_VERSION);
        body.append('poster', poster, `crystal-preview-${revision}.webp`);
        body.append('thumbnail', thumbnail, `crystal-preview-${revision}-square.webp`);
        const response = await this.requestJson(this.posterPath, { method: 'POST', body });
        if (response.poster) this.applyPoster(response.poster);
        this.persistHandoff();
        this.emitPreviewState('ready');
      } catch (_error) {
        this.posterUploadRevision = -1;
      }
    }

    applyPoster(poster) {
      const full = poster.full?.url || poster.poster_url || '';
      const thumbnail = poster.thumbnail?.url || poster.thumbnail_url || full;
      if (!full) return;
      this.posterFullUrl = full;
      this.posterThumbnailUrl = thumbnail;
      this.setLineProperty('_preview_image_url', full, true);
      this.setLineProperty('_preview_image_thumbnail_url', thumbnail, true);
    }

    invalidatePoster() {
      // The saved image was rendered with an older appearance; drop it so the current revision is captured again.
      this.posterFullUrl = '';
      this.posterThumbnailUrl = '';
      this.posterCaptureRevision = -1;
      this.posterUploadRevision = -1;
      this.posterRetryRevision = -1;
      const form = this.productForm;
      [
        'properties[_preview_image_url]',
        'properties[_preview_image_thumbnail_url]',
      ].forEach((name) => form?.querySelector(`[name="${name}"]`)?.remove());
    }

    showInitial() {
      this.dataset.state = 'initial';
      this.sample.hidden = false;
      this.working.hidden = true;
      this.rendererHost.hidden = true;
      this.statusHeading.textContent = this.dataset.copyInitialHeading;
      this.statusBody.textContent = '';
      this.changePhotoButton.textContent = this.dataset.copyChangePhoto;
      this.changePhotoButton.hidden = true;
      this.retryButton.hidden = true;
    }

    showWorking(heading, body, phase = 1, generationKind = 'initial') {
      this.dataset.state = 'processing';
      this.generationKind = generationKind;
      this.currentStageIndex = Math.max(1, Math.min(4, Number(phase) || 1));
      this.currentStageLabel = heading;
      if (!this.rendererFrame) this.sample.hidden = false;
      this.working.hidden = false;
      this.rendererHost.hidden = !this.rendererFrame && !this.pendingRendererFrame;
      if (this.sourceImage && this.objectUrl) this.sourceImage.src = this.objectUrl;
      if (this.overlayHeading) {
        this.overlayHeading.textContent = generationKind === 'update'
          ? this.dataset.copyUpdatingHeading
          : this.dataset.copyProcessingHeading;
      }
      if (this.overlayBody) this.overlayBody.textContent = heading;
      if (this.progress) {
        this.progress.dataset.step = String(this.currentStageIndex);
        this.progress.setAttribute('aria-valuenow', String(this.currentStageIndex));
      }
      if (this.progressTime) this.progressTime.textContent = this.dataset.copyProcessingTime;
      this.statusHeading.textContent = heading;
      this.statusBody.textContent = '';
      this.changePhotoButton.textContent = this.dataset.copyChangePhoto;
      this.changePhotoButton.hidden = false;
      this.retryButton.hidden = true;
      const signature = `${heading}|${body}|${this.currentStageIndex}`;
      if (signature !== this.lastWorkingSignature) {
        this.lastWorkingSignature = signature;
        this.emitPreviewState(Date.now() - this.pollStartedAt >= 60000 ? 'slow' : 'creating');
      }
    }

    showError(message, preserveViewer = false) {
      this.dataset.state = 'error';
      this.working.hidden = true;
      if (!preserveViewer) {
        this.sample.hidden = false;
        this.rendererHost.hidden = true;
      }
      this.statusHeading.textContent = this.dataset.copyErrorHeading;
      this.statusBody.textContent = message || this.dataset.copyErrorBody;
      this.changePhotoButton.hidden = true;
      this.retryButton.hidden = false;
      this.emitPreviewState('error');
    }

    async handlePreviewFailure(error) {
      this.stopPolling();
      window.clearTimeout(this.rendererSlowTimer);
      this.emitAnalytics('live_preview_v3_failed');
      try {
        const legacy = await this.legacyUploadPromise;
        if (legacy?.url) {
          this.activateV2Fallback(legacy);
          return;
        }
      } catch (_legacyError) {
        // The customer can retry the original browser upload.
      }
      this.showError(this.dataset.copyErrorBody);
    }

    activateV2Fallback(legacy) {
      this.setCartConfigurationPending(false);
      this.v3Active = false;
      this.hidden = true;
      this.closest('.product__preview')?.classList.remove('is-v3-active');
      this.closest('.theme-preview-popup')?.classList.remove('is-v3-active');
      if (
        typeof publish === 'function'
        && typeof PUB_SUB_EVENTS !== 'undefined'
        && PUB_SUB_EVENTS.filestackFileChoiced
      ) {
        publish(PUB_SUB_EVENTS.filestackFileChoiced, { data: legacy });
      }
    }

    async uploadLegacyPhoto(file, options = {}) {
      const generation = this.photoGeneration || 0;
      const asset = await window.NativePhotoCompat.upload(file);
      if (generation !== (this.photoGeneration || 0)) return null;
      this.nativeAsset = asset;
      return this.rememberLegacyPhoto({url:asset.url,asset_id:asset.asset_id,handle:asset.asset_id,
        filename:file.name,mimetype:asset.media_type,size:asset.bytes},file,options);
    }

    rememberLegacyPhoto(result, file) {
      const legacy = {
        url: result.url,
        handle: result.handle,
        filename: result.filename || file.name,
        mimetype: result.mimetype || file.type,
        size: result.size || file.size,
        source: result.source || 'local_file_system',
        uploadId: result.uploadId || result.handle,
      };
      this.legacyPhoto = legacy;
      this.setLineProperty('Photo', legacy.url, false);
      this.setLineProperty('_preview_image_url', this.posterFullUrl || legacy.url, true);
      this.setLineProperty(
        '_preview_image_thumbnail_url',
        this.posterThumbnailUrl || legacy.url,
        true,
      );
      this.emitPreviewState(this.dataset.state === 'ready' ? 'ready' : 'creating');
      this.persistHandoff();
      return legacy;
    }

    async requireNativePhotoStorage() {
      return window.NativePhotoCompat.requireNative();
    }

    setLineProperty(name, value, privateProperty) {
      const form = this.productForm || (
        this.dataset.productFormId ? document.getElementById(this.dataset.productFormId) : null
      );
      if (!form || !value) return;
      const propertyName = `properties[${name}]`;
      let input = form.querySelector(`[name="${propertyName}"]`);
      if (!input) {
        input = document.createElement('input');
        input.type = 'hidden';
        input.name = propertyName;
        if (privateProperty) input.dataset.livePreviewV3 = 'true';
        form.append(input);
      }
      input.value = value;
    }

    clearLineProperties() {
      const form = this.productForm;
      if (!form) return;
      [
        'properties[Photo]',
        'properties[_preview_image_url]',
        'properties[_preview_image_thumbnail_url]',
        'properties[_live_preview_v3_session_id]',
      ].forEach((name) => form.querySelector(`[name="${name}"]`)?.remove());
    }

    async handleFormSubmit(event) {
      if (!this.v3Active || this.allowNextSubmit || !this.file) {
        this.allowNextSubmit = false;
        return;
      }

      if (this.submissionInFlight) {
        event.preventDefault();
        event.stopImmediatePropagation();
        return;
      }

      if (this.selectionUpdateIsPending()) {
        event.preventDefault();
        event.stopImmediatePropagation();
        this.setCartConfigurationPending(true);
        return;
      }

      event.preventDefault();
      event.stopImmediatePropagation();
      const submitter = event.submitter;
      this.submissionInFlight = true;
      this.productForm?.closest('product-form')?.handleErrorMessage();
      const preparation = new AbortController();
      try {
        await window.StorefrontRequest.run(async (signal) => {
          if (!this.sessionId || !this.cartAssociationPath) await this.createPromise;
          if (!this.sessionId || !this.cartAssociationPath || !this.previewToken) {
            throw new Error(this.dataset.copyCartError);
          }

          const cart = await window.StorefrontRequest.json(`${window.routes?.root || '/'}cart.js`, {
            signal,
            credentials: 'same-origin',
            cache: 'no-store',
            headers: { Accept: 'application/json' },
          });
          if (!cart.token) throw new Error(this.dataset.copyCartError);

          if (this.associatedCartToken !== cart.token) {
            const payload = await this.requestJson(this.cartAssociationPath, {
              signal,
              method: 'POST',
              body: JSON.stringify({
                preview_token: this.previewToken,
                cart_token: cart.token,
              }),
            });
            signal.throwIfAborted();
            this.applyResponse(payload);
            this.associatedCartToken = cart.token;
          }

          signal.throwIfAborted();
        }, { timeoutMs: 150000, signal: preparation.signal });

        if (this.dataset.state === 'error') this.dataset.state = 'ready';
        this.retryButton.hidden = true;
        this.statusHeading.textContent = this.dataset.copyReadyHeading;
        this.statusBody.textContent = this.dataset.copyReadyBody;
        this.setLineProperty('_live_preview_v3_session_id', this.sessionId, true);
        this.emitAnalytics('live_preview_v3_add_to_cart');
        this.allowNextSubmit = true;
        if (submitter && this.productForm.requestSubmit) this.productForm.requestSubmit(submitter);
        else this.productForm.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
      } catch (error) {
        preparation.abort();
        this.allowNextSubmit = false;
        this.releaseSubmissionUi();
        this.showError(this.dataset.copyCartError, true);
      }
    }

    releaseSubmissionUi() {
      this.submissionInFlight = false;
    }

    async handleHomepageNavigation(event) {
      if (!this.v3Active || !this.file) return;
      const href = this.homeProductLink?.getAttribute('href');
      if (!href || href === '#') return;

      event.preventDefault();
      event.stopImmediatePropagation();
      try {
        if (!this.sessionId || !this.previewToken) await this.createPromise;
        if (!this.sessionId || !this.previewToken) {
          throw new Error(this.dataset.copyErrorBody);
        }
        this.persistHandoff();
        window.location.assign(href);
      } catch (error) {
        this.handlePreviewFailure(error);
      }
    }

    emitPreviewState(state) {
      const detail = {
        formId: this.dataset.productFormId,
        state,
        imageUrl: this.posterFullUrl || this.legacyPhoto?.url || this.objectUrl,
        thumbnailUrl: this.posterThumbnailUrl || this.legacyPhoto?.url || this.objectUrl,
        provider: 'v3',
        stageKey: this.currentStageKey,
        stageIndex: this.currentStageIndex,
        stageCount: 4,
        stageLabel: this.currentStageLabel,
        generationKind: this.generationKind,
      };
      const signature = JSON.stringify([
        state,
        detail.imageUrl,
        detail.thumbnailUrl,
        detail.stageKey,
        detail.stageIndex,
        detail.stageLabel,
        detail.generationKind,
      ]);
      if (signature === this.lastPreviewStateSignature) return;
      this.lastPreviewStateSignature = signature;
      document.dispatchEvent(new CustomEvent('ccm:preview-state', {
        detail,
      }));
    }

    emitAnalytics(eventName) {
      const lifecycleEvents = new Set([
        'live_preview_v3_upload_started',
        'live_preview_v3_session_created',
        'live_preview_v3_ready',
        'live_preview_v3_failed',
      ]);
      if (lifecycleEvents.has(eventName)) {
        if (this.analyticsEmitted.has(eventName)) return;
        this.analyticsEmitted.add(eventName);
      }
      const selection = this.desiredSelection || {};
      const variantId = this.productForm?.querySelector('[name="id"]')?.value || '';
      const payload = {
        event: eventName,
        live_preview_store: this.dataset.store || 'cmk',
        live_preview_version: 'v3',
        storefront_epoch: 'cmk-live-preview-v3-20260820',
        variant_id: variantId,
        live_preview_crystal_sku: selection.crystal_sku || '',
        live_preview_base_sku: selection.base_sku || 'none',
        live_preview_text_present: Boolean(selection.engraving_text),
        live_preview_wall_time_ms: this.pollStartedAt ? Date.now() - this.pollStartedAt : 0,
        live_preview_session_id: this.sessionId || '',
      };
      if (Array.isArray(window.dataLayer)) window.dataLayer.push(payload);
      try {
        if (typeof window.Shopify?.analytics?.publish === 'function') {
          window.Shopify.analytics.publish(eventName, {
            store: payload.live_preview_store,
            previewVersion: payload.live_preview_version,
            storefrontEpoch: payload.storefront_epoch,
            variantId,
            crystalSku: payload.live_preview_crystal_sku,
            baseSku: payload.live_preview_base_sku,
            textPresent: payload.live_preview_text_present,
            wallTimeMs: payload.live_preview_wall_time_ms,
            previewSessionId: payload.live_preview_session_id,
          });
        }
      } catch (error) {
        console.warn('[CMK Live Preview] Analytics delivery failed', error);
      }
      this.dispatchEvent(new CustomEvent(eventName, { detail: payload, bubbles: true }));
    }

    persistHandoff() {
      if (!this.sessionId || !this.previewToken) return;
      const record = {
        version: 1,
        savedAt: Date.now(),
        sessionId: this.sessionId,
        previewToken: this.previewToken,
        statusPath: this.statusPath,
        configurePath: this.configurePath,
        cartAssociationPath: this.cartAssociationPath,
        rendererPath: this.rendererPath,
        posterPath: this.posterPath,
        appearanceVersion: APPEARANCE_VERSION,
        posterFullUrl: this.posterFullUrl,
        posterThumbnailUrl: this.posterThumbnailUrl,
        status: this.statusValue,
        inferenceStatus: this.inferenceStatus,
        compilationRevision: this.currentRevision,
        selection: {
          crystal_sku: this.desiredSelection.crystal_sku,
          orientation: this.desiredSelection.orientation,
          base_sku: this.desiredSelection.base_sku,
          engraving_text: this.desiredSelection.engraving_text,
          engraving_font: this.desiredSelection.engraving_font,
        },
        legacyPhoto: this.legacyPhoto,
      };
      try {
        sessionStorage.setItem(HANDOFF_KEY, JSON.stringify(record));
      } catch (_error) {
        // The same-page preview remains usable when session storage is blocked.
      }
    }

    restoreHandoff() {
      let record;
      try {
        record = safeJsonParse(sessionStorage.getItem(HANDOFF_KEY));
      } catch (_error) {
        return;
      }
      if (
        !record
        || record.version !== 1
        || !record.sessionId
        || !record.previewToken
        || Date.now() - Number(record.savedAt || 0) > HANDOFF_MAX_AGE_MS
      ) return;

      this.sessionId = record.sessionId;
      this.previewToken = record.previewToken;
      this.statusPath = record.statusPath || '';
      this.configurePath = record.configurePath || '';
      this.cartAssociationPath = record.cartAssociationPath || '';
      this.rendererPath = record.rendererPath || '';
      this.posterPath = record.posterPath || '';
      this.posterFullUrl = record.appearanceVersion === APPEARANCE_VERSION ? record.posterFullUrl || '' : '';
      this.posterThumbnailUrl = record.appearanceVersion === APPEARANCE_VERSION ? record.posterThumbnailUrl || '' : '';
      this.statusValue = record.status || '';
      this.inferenceStatus = record.inferenceStatus || '';
      this.currentRevision = Number(record.compilationRevision ?? -1);
      this.legacyPhoto = record.legacyPhoto || null;
      this.file = { restored: true };
      this.legacyUploadPromise = Promise.resolve(this.legacyPhoto);
      if (this.legacyPhoto?.url) {
        this.objectUrl = this.legacyPhoto.url;
        this.setLineProperty('Photo', this.legacyPhoto.url, false);
        this.setLineProperty(
          '_preview_image_url',
          this.posterFullUrl || this.legacyPhoto.url,
          true,
        );
        this.setLineProperty(
          '_preview_image_thumbnail_url',
          this.posterThumbnailUrl || this.legacyPhoto.url,
          true,
        );
      }

      const restoredSelection = {
        ...this.desiredSelection,
        ...(record.selection || {}),
        engraving_text: this.desiredSelection.engraving_text
          || record.selection?.engraving_text
          || '',
      };
      this.lastSentSignature = this.selectionSignature(restoredSelection);
      const currentSelectionSignature = this.selectionSignature(this.desiredSelection);
      this.generationKind = 'initial';
      this.currentStageIndex = 1;
      this.currentStageKey = 'intake';
      this.currentStageLabel = this.stageLabels.intake || this.dataset.copyProcessingHeading;
      this.showWorking(
        this.currentStageLabel,
        this.dataset.copyProcessingBody,
        1,
        this.generationKind,
      );
      this.emitPreviewState('creating');
      this.pollStartedAt = Date.now();
      if (currentSelectionSignature !== this.lastSentSignature) this.scheduleConfigure(0);
      if (this.statusPath) {
        this.statusValue = '';
        this.schedulePoll();
      }
    }

    focusViewer() {
      this.setAttribute('tabindex', '-1');
      this.scrollIntoView({
        behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
        block: 'center',
      });
      this.focus({ preventScroll: true });
    }
  }

  customElements.define('ccm-live-preview-v3', CcmLivePreviewV3);
})();
